import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  FileSpreadsheet,
  FileText,
  Plus,
  Printer,
  RefreshCw,
  Save,
  Trash2,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { z } from 'zod';
import { api } from '@/lib/api-client';
import { Button, Card, Chip } from '@/ui/primitives';
import { numeroFlexivel } from '../numero-flexivel';
import { RTAQuestionario } from './RTAQuestionario';
import { ComentariosSection } from '@/modules/kanban/components/DetailDrawer';

const rowSchema = z
  .object({
    vinculo: z.string().nullish(),
    contrato_id: z.coerce.number().nullish(),
    contrato_tipo: z.string().nullish(),
    area_conjunta: z.boolean().optional(),
    area_grupo_municipio: z.string().optional(),
    area_grupo_uf: z.string().optional(),
    area_contrato_ha: z.union([z.string(), z.number()]).nullish(),
    culturas_permitidas: z.string().nullish(),
    id: z.coerce.number().nullish(),
    denominacao: z.string().nullish(),
    matricula: z.string().nullish(),
    municipio: z.string().nullish(),
    uf: z.string().nullish(),
    area_total_ha: z.union([z.string(), z.number()]).nullish(),
    car_area_consolidada: z.union([z.string(), z.number()]).nullish(),
    data_vencimento: z.string().nullish(),
    data_inicio: z.string().nullish(),
    arrendador_nome: z.string().nullish(),
    proprietario_nome: z.string().nullish(),
    car_longitude: z.string().nullish(),
    car_latitude: z.string().nullish(),
    car_app: z.union([z.string(), z.number()]).nullish(),
    car_reserva_legal: z.union([z.string(), z.number()]).nullish(),
    car_vegetacao_nativa: z.union([z.string(), z.number()]).nullish(),
    car_codigo: z.string().nullish(),
    ccir_numero: z.string().nullish(),
    cib: z.string().nullish(),
    radam_tipo_solo: z.string().nullish(),
    radam_vegetacao: z.string().nullish(),
    radam_relevo: z.string().nullish(),
    radam_potencialidade_agricola: z.string().nullish(),
    benfeitorias: z
      .array(
        z
          .object({
            id: z.string().nullish(),
            discriminacao: z.string().nullish(),
            unidade: z.string().nullish(),
            quantidade: z.union([z.string(), z.number()]).nullish(),
            valor_unitario: z.union([z.string(), z.number()]).nullish(),
            valor_total: z.union([z.string(), z.number()]).nullish(),
          })
          .passthrough(),
      )
      .nullish(),
  })
  .passthrough();

type BBImovelRow = z.infer<typeof rowSchema>;
type Benfeitoria = {
  id?: string;
  discriminacao: string;
  unidade?: string;
  quantidade: string;
  valor_unitario: string;
  valor_total?: number | string | null;
  fonte?: string | null;
  trecho?: string | null;
  observacao?: string | null;
};

const resumoSchema = z.object({
  processo: z
    .object({
      cliente_cpf: z.string().nullish(),
      nome: z.string().nullish(),
      municipio: z.string().nullish(),
      uf: z.string().nullish(),
      telefone: z.string().nullish(),
      email: z.string().nullish(),
      profissao: z.string().nullish(),
      estado_civil: z.string().nullish(),
      banco_nome: z.string().nullish(),
      banco_agencia: z.string().nullish(),
      banco_conta: z.string().nullish(),
      proxima_acao: z.string().nullish(),
      observacoes: z.string().nullish(),
      instituicao_nome: z.string().nullish(),
      programa_nome: z.string().nullish(),
    })
    .passthrough(),
  totais: z.object({
    imoveis_proprios: z.coerce.number(),
    arrendamentos: z.coerce.number(),
    comodatos: z.coerce.number(),
    area_base_ha: z.coerce.number(),
    documentos: z.coerce.number(),
  }),
  imoveis: z.array(rowSchema),
  checklist: z.array(
    z
      .object({
        nome_documento: z.string(),
        status: z.string().nullish(),
        arquivo_url: z.string().nullish(),
        documento_id: z.coerce.number().nullish(),
        observacoes: z.string().nullish(),
      })
      .passthrough(),
  ),
});

function area(v: unknown): string {
  if (v == null || v === '') return '—';
  const raw = typeof v === 'string' ? v.replace(/\s*ha\s*$/i, '').trim() : v;
  if (typeof raw === 'string' && !/\d/.test(raw)) return '—';
  const n = numeroFlexivel(raw);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 }) + ' ha';
}
function labelVinculo(v?: string | null) {
  return v === 'P' ? 'Próprio' : v === 'A' ? 'Arrendado' : v === 'C' ? 'Comodato' : '—';
}
function fichaTipo(v?: string | null) {
  return v === 'P' ? 'Ficha de imóvel próprio' : 'Ficha de imóvel de terceiros';
}
function keyImovel(r: BBImovelRow, idx: number) {
  return `${r.vinculo || 'X'}-${r.id || 'sem'}-${r.contrato_id || 'proprio'}-${idx}`;
}
function keyFicha(r: BBImovelRow) {
  return `${r.vinculo || 'X'}-${r.id || 'sem'}-${r.contrato_id || 'proprio'}`;
}
function areaBase(r: BBImovelRow) {
  return r.vinculo === 'P'
    ? (r.car_area_consolidada ?? r.area_total_ha)
    : (r.area_contrato_ha ?? r.car_area_consolidada ?? r.area_total_ha);
}

function nBR(v: unknown): number {
  return numeroFlexivel(v);
}
function moeda(v: unknown): string {
  const n = nBR(v);
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}
function benfeitoriasIniciais(r: BBImovelRow): Benfeitoria[] {
  const arr = Array.isArray(r.benfeitorias) ? r.benfeitorias : [];
  return arr.map((b, idx) => ({
    id: String(b.id || `benf-${idx}`),
    discriminacao: String(b.discriminacao || ''),
    unidade: String(b.unidade || ''),
    quantidade: String(b.quantidade ?? ''),
    valor_unitario: String(b.valor_unitario ?? ''),
    valor_total: b.valor_total ?? null,
  }));
}

function radamResumo(r: BBImovelRow) {
  const vals = [
    r.radam_tipo_solo ? `Solo: ${r.radam_tipo_solo}` : null,
    r.radam_vegetacao ? `Vegetação: ${r.radam_vegetacao}` : null,
    r.radam_relevo ? `Relevo: ${r.radam_relevo}` : null,
    r.radam_potencialidade_agricola ? `Potencialidade: ${r.radam_potencialidade_agricola}` : null,
  ].filter(Boolean);
  return vals.length ? vals.join(' · ') : 'Buscar tipo de solo e vegetação pela base/poligonal CAR';
}

const semoventesPenhorModelo = [
  ['Banco Bradesco S.A.', '60.746.948/0001-12', '30/09/2025', '250 bovinos Nelore', 'Lote nº 03-A da Gleba nº 16', 'R$ 750.000,00'],
  ['Banco da Amazônia S.A.', '04.902.979/0001-44', '17/10/2025', '78 garrotas bovinas Nelore, 13 a 24 meses', 'Lote Rural nº 21 da Gleba nº 18', 'R$ 182.520,00'],
  ['Banco do Brasil S.A.', '00.000.000/4349-49', '04/11/2025', '424 novilhas, 237 novilhos e 460 novilhos Nelore, 13 a 36 meses', 'Lote nº 44-B da Gleba nº 19', 'R$ 3.335.700,00'],
] as const;

const semoventesRtaPecuariaModelo = [
  'Nº RTA 175 — Bovinocultura / BOI GORDO — fase CRIA/RECRIA/ENGORDA: sistema extensivo, independente, tecnologia média, mão de obra contratada. Usar quando a renda principal vier do ciclo completo de corte.',
  'Nº RTA 176 — Bovinocultura / BOI MAGRO ZEBUÍNO — fase CRIA/RECRIA: sistema extensivo, independente, tecnologia média, mão de obra contratada. Compatível com reposição e recria do rebanho Nelore.',
  'Nº RTA 177 — Bovinocultura / BEZERRO ZEBUÍNO (CORTE) — fase CRIA: sistema extensivo, independente, tecnologia média, mão de obra contratada. Usar para renda de cria/desmama vinculada às matrizes.',
  'Nº RTA 179 — Bovinocultura / BOI GORDO — fase RECRIA/ENGORDA: sistema extensivo, independente, tecnologia média, mão de obra contratada. Compatível com giro dos machos de 13 a 36 meses.',
  'Nº RTA 187 — Bovinocultura / NOVILHA GORDA — fase RECRIA/ENGORDA: sistema extensivo, independente, tecnologia média, mão de obra contratada. Compatível com fêmeas jovens destinadas à recria/engorda.',
  'Observação operacional: animais em penhor não entram como disponibilidade livre; animais não constantes na certidão/cadastro BB devem ser tratados como quitados (S = Q). Fonte: Dados RTA BB — aba pecuária.',
] as const;

export function BBResumo({ cardId, banco = 'BB' }: { cardId: string; banco?: 'BB' | 'BASA' }) {
  const isBB = banco === 'BB';
  const qc = useQueryClient();
  const [openMunicipios, setOpenMunicipios] = useState<Record<string, boolean>>({});
  const [openImovel, setOpenImovel] = useState<string | null>(null);
  const [fichasGeradas, setFichasGeradas] = useState<Record<string, string>>({});
  const [abaBB, setAbaBB] = useState<
    'questionario' | 'resumo' | 'cadastro' | 'imovel_bb' | 'semoventes' | 'documentos'
  >(isBB ? 'questionario' : 'cadastro');
  const { data, isLoading, error } = useQuery({
    queryKey: ['credito-bb-resumo', cardId],
    queryFn: ({ signal }) =>
      api(`/api/v2/processos-credito/${cardId}/bb-resumo`, { schema: resumoSchema, signal }),
    enabled: !!cardId,
    staleTime: 20_000,
  });
  const gerarArea = useMutation({
    mutationFn: () =>
      api(`/api/v2/processos-credito/${cardId}/area-beneficiada/planilha`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['credito-bb-resumo', cardId] });
      void qc.invalidateQueries({ queryKey: ['credito-checklist', cardId] });
    },
  });
  const gerarFichaImovel = useMutation({
    mutationFn: ({ row: r }: { row: BBImovelRow; targetWindow: Window | null }) => {
      const qs = new URLSearchParams({ imovel_id: String(r.id), vinculo: String(r.vinculo || '') });
      if (r.contrato_id != null) qs.set('contrato_id', String(r.contrato_id));
      return api(`/api/v2/processos-credito/${cardId}/ficha-imovel-bb/pdf?${qs.toString()}`, {
        method: 'POST',
      }) as Promise<{ arquivo_url?: string }>;
    },
    onSuccess: (res, { row, targetWindow }) => {
      void qc.invalidateQueries({ queryKey: ['credito-bb-resumo', cardId] });
      void qc.invalidateQueries({ queryKey: ['credito-checklist', cardId] });
      if (res?.arquivo_url) {
        setFichasGeradas((cur) => ({ ...cur, [keyFicha(row)]: res.arquivo_url! }));
        if (targetWindow && !targetWindow.closed) targetWindow.location.href = res.arquivo_url;
        else window.open(res.arquivo_url, '_blank', 'noopener,noreferrer');
      }
    },
    onError: (_error, { targetWindow }) => {
      if (targetWindow && !targetWindow.closed) targetWindow.close();
    },
  });
  const gerarRelatorioSemoventesPdf = useMutation({
    mutationFn: () =>
      api(`/api/v2/processos-credito/${cardId}/semoventes-bb/pdf`, { method: 'POST' }) as Promise<{
        arquivo_url?: string;
      }>,
    onSuccess: async (res) => {
      void qc.invalidateQueries({ queryKey: ['credito-bb-resumo', cardId] });
      void qc.invalidateQueries({ queryKey: ['credito-checklist', cardId] });
      if (res?.arquivo_url) {
        try {
          const pdf = await fetch(res.arquivo_url, {
            credentials: 'include',
            headers: { Accept: 'application/pdf' },
          });
          if (!pdf.ok) throw new Error(`HTTP ${pdf.status}`);
          const blob = await pdf.blob();
          const blobUrl = URL.createObjectURL(blob);
          const opened = window.open(blobUrl, '_blank', 'noopener,noreferrer');
          if (!opened) window.location.assign(blobUrl);
          setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
        } catch (e) {
          console.error('[bb-semoventes] falha ao abrir PDF gerado', e);
          window.open(res.arquivo_url, '_blank', 'noopener,noreferrer');
        }
      }
    },
  });
  const salvarBenfeitorias = useMutation({
    mutationFn: ({ row, benfeitorias }: { row: BBImovelRow; benfeitorias: Benfeitoria[] }) =>
      api(`/api/v2/processos-credito/${cardId}/imoveis/${row.id}/benfeitorias`, {
        method: 'PATCH',
        body: { benfeitorias },
      }) as Promise<{ benfeitorias?: Benfeitoria[] }>,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['credito-bb-resumo', cardId] });
      void qc.invalidateQueries({ queryKey: ['credito-checklist', cardId] });
    },
  });

  const buscarSugestoesBenfeitorias = useMutation({
    mutationFn: (row: BBImovelRow) =>
      api(`/api/v2/processos-credito/${cardId}/imoveis/${row.id}/benfeitorias-sugestoes`, {
        schema: z.object({
          sugestoes: z.array(
            z
              .object({
                id: z.string().nullish(),
                discriminacao: z.string(),
                unidade: z.string().nullish(),
                quantidade: z.union([z.string(), z.number()]).nullish(),
                valor_unitario: z.union([z.string(), z.number()]).nullish(),
                valor_total: z.union([z.string(), z.number()]).nullish(),
                fonte: z.string().nullish(),
                trecho: z.string().nullish(),
                observacao: z.string().nullish(),
              })
              .passthrough(),
          ),
        }),
      }) as Promise<{ sugestoes: Benfeitoria[] }>,
  });

  const gerarPdf = useMutation({
    mutationFn: () =>
      api(`/api/v2/processos-credito/${cardId}/area-beneficiada/pdf`, {
        method: 'POST',
      }) as Promise<{ arquivo_url?: string }>,
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['credito-bb-resumo', cardId] });
      if (res?.arquivo_url) window.location.assign(res.arquivo_url);
    },
  });
  const gruposMunicipio = useMemo(() => {
    if (!data) return [];
    const m = new Map<
      string,
      { municipio: string; uf: string; area: number; rows: typeof data.imoveis }
    >();
    const countedContracts = new Set<number>();
    for (const r of data.imoveis) {
      const municipio = r.area_grupo_municipio || r.municipio || 'NÃO INFORMADO';
      const uf = r.area_grupo_uf || r.uf || 'UF não informada';
      const key = `${municipio}/${uf}`;
      const current = m.get(key) ?? { municipio, uf, area: 0, rows: [] as typeof data.imoveis };
      const n = Number(areaBase(r));
      const repeated = r.vinculo !== 'P' && r.contrato_id != null && countedContracts.has(r.contrato_id);
      if (!repeated && Number.isFinite(n)) current.area += n;
      if (r.vinculo !== 'P' && r.contrato_id != null) countedContracts.add(r.contrato_id);
      current.rows.push(r);
      m.set(key, current);
    }
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0], 'pt-BR'));
  }, [data]);

  if (isLoading) return <p className="text-sm text-text-muted py-4">Montando cadastro…</p>;
  if (error || !data)
    return <p className="text-sm text-danger py-4">Falha ao carregar cadastro.</p>;
  const p = data.processo;
  const areaDoc = data.checklist.find((i) => i.nome_documento.toLowerCase().includes('matrícula'));
  const produtoRta =
    typeof p.cultura_atividade === 'string' && p.cultura_atividade
      ? p.cultura_atividade
      : (isBB ? 'soja/milho conforme RTA' : 'Atividade não informada');
  const tecnologiaRta = isBB ? 'RTA: preencher/confirmar tecnologia no Portal BB' : 'Tecnologia: confirmar no cadastro produtivo';
  return (
    <div className="space-y-4">
      <Card padding="md" className="space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[0.72rem] uppercase tracking-wide text-text-muted font-semibold">
              {banco} — atualização de cadastro rural/produtivo
            </div>
            <div className="text-sm font-semibold text-text">
              {p.nome ?? 'Cliente'} · {p.cliente_cpf}
            </div>
            <p className="text-xs text-text-muted mt-1">
              Preenchimento guiado para reduzir digitação: respostas editáveis, cálculos automáticos
              e documentos/checklist no mesmo fluxo.
              {!isBB && ' Dados comuns do cliente e dos imóveis são reaproveitados; questionário RTA e relatório BB de semoventes permanecem exclusivos do BB.'}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap rounded-lg border border-border bg-surface2 p-1">
            {(
              [
                ['questionario', 'Questionário'],
                ['resumo', 'Resumo ' + banco],
                ['cadastro', 'Cadastro'],
                ['imovel_bb', 'Cadastro de imóvel ' + banco],
                ['semoventes', 'Semoventes'],
                ['documentos', 'Documentos'],
              ] as const
            ).filter(([id]) => isBB || !['questionario', 'semoventes'].includes(id)).map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => setAbaBB(id)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold ${abaBB === id ? 'bg-elevated text-text shadow-sm' : 'text-text-muted hover:text-text'}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {isBB && abaBB === 'questionario' && (
          <>
            <RTAQuestionario cardId={cardId} />
            <ComentariosSection dominio="credito" cardId={cardId} />
          </>
        )}

        {abaBB === 'resumo' && (
          <div className="space-y-4">
            <div className={isBB ? "flex gap-2 flex-wrap" : "hidden"}>
              <Button
                variant="primary"
                size="sm"
                onClick={() => gerarArea.mutate()}
                disabled={gerarArea.isPending}
              >
                <RefreshCw size={13} strokeWidth={1.5} />{' '}
                {gerarArea.isPending ? 'Gerando…' : 'Gerar/Atualizar Área Beneficiada'}
              </Button>
              {areaDoc?.arquivo_url && (
                <Button variant="secondary" size="sm" asChild>
                  <a href={areaDoc.arquivo_url} target="_blank" rel="noreferrer">
                    <FileSpreadsheet size={13} /> Abrir planilha
                  </a>
                </Button>
              )}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => gerarPdf.mutate()}
                disabled={gerarPdf.isPending}
              >
                <FileText size={13} /> {gerarPdf.isPending ? 'Gerando PDF…' : 'Gerar PDF dos dados'}
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
              <Chip tone="neutral" size="sm">
                Próprios: {data.totais.imoveis_proprios}
              </Chip>
              <Chip tone="info" size="sm">
                Arrend.: {data.totais.arrendamentos}
              </Chip>
              <Chip tone="accent" size="sm">
                Comod.: {data.totais.comodatos}
              </Chip>
              <Chip tone="ok" size="sm">
                Área base: {area(data.totais.area_base_ha)}
              </Chip>
              <Chip tone="neutral" size="sm">
                Docs: {data.totais.documentos}
              </Chip>
            </div>
            <div>
              <h3 className="text-[0.78rem] font-semibold uppercase tracking-wide text-text-muted mb-3">
                Imóvel beneficiado = todo o rol do cliente
              </h3>
              <div className="space-y-2">
                {gruposMunicipio.map(([key, grupo]) => {
                  const open = openMunicipios[key] ?? false;
                  return (
                    <section
                      key={key}
                      className="border border-border rounded-lg overflow-hidden bg-surface"
                    >
                      <button
                        type="button"
                        onClick={() => setOpenMunicipios((v) => ({ ...v, [key]: !open }))}
                        className="w-full px-3 py-2 flex items-center justify-between gap-3 text-left hover:bg-surface2"
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-text flex items-center gap-1">
                            <ChevronRight
                              size={14}
                              className={
                                open ? 'rotate-90 transition-transform' : 'transition-transform'
                              }
                            />
                            {grupo.municipio}/{grupo.uf} = {area(grupo.area)} área beneficiada
                          </div>
                          <div className="text-xs text-text-muted truncate">
                            Produto: {produtoRta} · {tecnologiaRta} · {grupo.rows.length}{' '}
                            imóvel(is)/vínculo(s)
                          </div>
                        </div>
                        <Chip tone="info" size="sm">
                          ver imóveis
                        </Chip>
                      </button>
                      {open && (
                        <div className="overflow-auto border-t border-border">
                          <table className="w-full text-xs border-collapse">
                            <thead>
                              <tr className="text-left text-text-2 border-b border-border bg-surface2">
                                <th className="py-2 px-3">Vínculo</th>
                                <th className="py-2 px-3">Município/UF</th>
                                <th className="py-2 px-3">Matrícula</th>
                                <th className="py-2 px-3">Imóvel</th>
                                <th className="py-2 px-3">Área cadastrada</th>
                                <th className="py-2 px-3">CAR/CCIR/CIB</th>
                              </tr>
                            </thead>
                            <tbody>
                              {grupo.rows.map((r, idx) => (
                                <tr
                                  key={`${r.vinculo}-${r.id}-${idx}`}
                                  className="border-b border-border align-top"
                                >
                                  <td className="py-2 px-3 font-medium">
                                    {labelVinculo(r.vinculo)}
                                  </td>
                                  <td className="py-2 px-3">
                                    {[r.municipio, r.uf].filter(Boolean).join('/') || '—'}
                                  </td>
                                  <td className="py-2 px-3">{r.matricula ?? '—'}</td>
                                  <td className="py-2 px-3 min-w-[220px]">
                                    {r.denominacao ?? '—'}
                                    {r.contrato_tipo ? (
                                      <div className="text-text-muted">{r.contrato_tipo}</div>
                                    ) : null}
                                  </td>
                                  <td className="py-2 px-3">{r.area_conjunta ? `Área conjunta: ${area(r.area_contrato_ha)} (sem divisão por matrícula)` : area(areaBase(r))}</td>
                                  <td className="py-2 px-3 min-w-[220px] text-text-muted">
                                    {[
                                      r.car_codigo ? `CAR ${r.car_codigo}` : null,
                                      r.ccir_numero ? `CCIR ${r.ccir_numero}` : null,
                                      r.cib ? `CIB ${r.cib}` : null,
                                    ]
                                      .filter(Boolean)
                                      .join(' · ') || '—'}
                                    <div className="mt-1 text-[0.7rem] text-text-muted">
                                      {radamResumo(r)}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </section>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {abaBB === 'cadastro' && (
          <div className="grid md:grid-cols-2 gap-3 text-sm">
            <div className="rounded-lg border border-border bg-surface p-3 space-y-1">
              <div className="font-semibold text-text">Dados do cliente</div>
              <div>
                <b>CPF:</b> {p.cliente_cpf ?? '—'}
              </div>
              <div>
                <b>Município/UF:</b> {[p.municipio, p.uf].filter(Boolean).join('/') || '—'}
              </div>
              <div>
                <b>Contato:</b> {[p.telefone, p.email].filter(Boolean).join(' · ') || '—'}
              </div>
              <div>
                <b>Profissão/estado civil:</b>{' '}
                {[p.profissao, p.estado_civil].filter(Boolean).join(' · ') || '—'}
              </div>
            </div>
            <div className="rounded-lg border border-border bg-surface p-3 space-y-1">
              <div className="font-semibold text-text">Dados bancários e operação</div>
              <div>
                <b>Banco/conta:</b>{' '}
                {[p.banco_nome, p.banco_agencia, p.banco_conta].filter(Boolean).join(' · ') ||
                  'não informado'}
              </div>
              <div>
                <b>Programa:</b> {p.programa_nome ?? '—'}
              </div>
              <div>
                <b>Próxima ação:</b> {p.proxima_acao ?? '—'}
              </div>
              {p.cliente_cpf && (
                <Button variant="ghost" size="sm" asChild>
                  <a href={`/clientes/${encodeURIComponent(p.cliente_cpf)}`}>
                    <ExternalLink size={13} /> Abrir cadastro do cliente
                  </a>
                </Button>
              )}
            </div>
          </div>
        )}

        {abaBB === 'imovel_bb' && (
          <div className="space-y-3">
            <div>
              <div className="text-[0.72rem] uppercase tracking-wide text-text-muted font-semibold">
                Cadastro de imóvel {banco}
              </div>
              <div className="text-sm font-semibold text-text">
                Dados para lançamento/validação do imóvel no {isBB ? 'Portal BB' : 'Banco da Amazônia'}
              </div>
              <p className="text-xs text-text-muted mt-1">
                Base: rol do cliente no Hub. Conferir vínculo, matrícula/CAR/CCIR/CIB e área antes
                do lançamento no {banco}.
              </p>
            </div>
            <div className="space-y-2">
              {data.imoveis.map((r, idx) => {
                const k = keyImovel(r, idx);
                const open = openImovel === k;
                return (
                  <div
                    key={`${k}-bb`}
                    className="rounded-lg border border-border bg-surface text-xs overflow-hidden"
                  >
                    <button
                      type="button"
                      onClick={() => setOpenImovel(open ? null : k)}
                      className="w-full p-3 flex items-center justify-between gap-3 text-left hover:bg-surface2"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-text flex items-center gap-1">
                          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          <span className="truncate">
                            {r.denominacao ?? r.car_codigo ?? `Imóvel ${idx + 1}`}
                          </span>
                        </div>
                        <div className="text-text-muted truncate">
                          {labelVinculo(r.vinculo)} · {fichaTipo(r.vinculo)} ·{' '}
                          {[r.municipio, r.uf].filter(Boolean).join('/') ||
                            'Município não informado'}{' '}
                          · Matrícula {r.matricula ?? '—'}
                        </div>
                      </div>
                      <div className="flex gap-2 items-center shrink-0">
                        <Chip tone={r.vinculo === 'P' ? 'ok' : 'info'} size="sm">
                          {labelVinculo(r.vinculo)}
                        </Chip>
                        <Chip tone="neutral" size="sm">
                          {r.area_conjunta ? `Área conjunta: ${area(r.area_contrato_ha)} (sem divisão por matrícula)` : area(areaBase(r))}
                        </Chip>
                      </div>
                    </button>
                    {open && (
                      <div className="p-3 border-t border-border space-y-3">
                        <div className="flex gap-2 flex-wrap">
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => {
                              const targetWindow = window.open('', '_blank');
                              if (targetWindow) {
                                targetWindow.opener = null;
                                targetWindow.document.title = 'Gerando ficha…';
                                targetWindow.document.body.textContent = 'Gerando ficha…';
                              }
                              gerarFichaImovel.mutate({ row: r, targetWindow });
                            }}
                            disabled={gerarFichaImovel.isPending || !r.id}
                          >
                            <Printer size={13} />{' '}
                            {gerarFichaImovel.isPending
                              ? 'Gerando…'
                              : 'Gerar e abrir ficha individual'}
                          </Button>
                          {fichasGeradas[keyFicha(r)] && (
                            <Button variant="secondary" size="sm" asChild>
                              <a href={fichasGeradas[keyFicha(r)]} target="_blank" rel="noreferrer">
                                <ExternalLink size={13} /> Abrir ficha gerada
                              </a>
                            </Button>
                          )}
                          <Chip tone="neutral" size="sm">
                            Modelo: {fichaTipo(r.vinculo)}
                          </Chip>
                          {!r.car_codigo && (
                            <Chip tone="warn" size="sm">
                              CAR pendente
                            </Chip>
                          )}
                        </div>
                        {gerarFichaImovel.error && (
                          <p className="text-xs text-danger">
                            {gerarFichaImovel.error instanceof Error
                              ? gerarFichaImovel.error.message
                              : 'Falha ao gerar ficha individual.'}
                          </p>
                        )}
                        <div className="grid md:grid-cols-4 gap-2">
                          <div className="rounded border border-border bg-muted/20 p-2">
                            <b>Matrícula/Registro</b>
                            <br />
                            {r.matricula ?? '—'}
                          </div>
                          <div className="rounded border border-border bg-muted/20 p-2">
                            <b>CAR</b>
                            <br />
                            {r.car_codigo ?? '—'}
                          </div>
                          <div className="rounded border border-border bg-muted/20 p-2">
                            <b>CCIR</b>
                            <br />
                            {r.ccir_numero ?? '—'}
                          </div>
                          <div className="rounded border border-border bg-muted/20 p-2">
                            <b>CIB/ITR</b>
                            <br />
                            {r.cib ?? '—'}
                          </div>
                          <div className="rounded border border-border bg-muted/20 p-2">
                            <b>Centroide CAR</b>
                            <br />
                            {[r.car_latitude, r.car_longitude].filter(Boolean).join(' / ') ||
                              'pendente'}
                          </div>
                          <div className="rounded border border-border bg-muted/20 p-2">
                            <b>Área total</b>
                            <br />
                            {area(r.area_total_ha)}
                          </div>
                          <div className="rounded border border-border bg-muted/20 p-2">
                            <b>Área consolidada CAR</b>
                            <br />
                            {area(r.car_area_consolidada)}
                            {r.car_codigo && data.imoveis.some((other) => other.id !== r.id && other.car_codigo === r.car_codigo) && (
                              <p className="mt-1 text-text-muted">Total do CAR compartilhado; não é área individual desta matrícula.</p>
                            )}
                          </div>
                          <div className="rounded border border-border bg-muted/20 p-2">
                            <b>{r.area_conjunta ? 'Área contratada do conjunto' : 'Área contratada/cedida'}</b>
                            <br />
                            {area(r.area_contrato_ha)}
                            {r.area_conjunta && (
                              <p className="mt-1 text-text-muted">Área individual de plantio não delimitada; total do contrato, contado uma única vez.</p>
                            )}
                          </div>
                        </div>
                        <div className="grid md:grid-cols-3 gap-2">
                          <div className="rounded border border-border bg-muted/20 p-2">
                            <b>APP</b>
                            <br />
                            {area(r.car_app)}
                          </div>
                          <div className="rounded border border-border bg-muted/20 p-2">
                            <b>Reserva legal</b>
                            <br />
                            {area(r.car_reserva_legal)}
                          </div>
                          <div className="rounded border border-border bg-muted/20 p-2">
                            <b>Vegetação nativa</b>
                            <br />
                            {area(r.car_vegetacao_nativa)}
                          </div>
                        </div>
                        <div className="rounded border border-border bg-muted/20 p-2 text-text-muted">
                          <b>RADAMBRASIL/IBGE:</b> {radamResumo(r)}
                        </div>
                        {r.vinculo === 'P' && (
                          <BenfeitoriasEditor
                            row={r}
                            saving={salvarBenfeitorias.isPending}
                            suggesting={buscarSugestoesBenfeitorias.isPending}
                            onSuggest={async () => {
                              const res = await buscarSugestoesBenfeitorias.mutateAsync(r);
                              return res.sugestoes || [];
                            }}
                            onSave={(benfeitorias) =>
                              salvarBenfeitorias.mutateAsync({ row: r, benfeitorias })
                            }
                          />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {isBB && abaBB === 'semoventes' && (
          <div className="space-y-3">
            <div className="rounded-lg border border-border bg-surface p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-sm font-semibold text-text">Semoventes</div>
                  <p className="text-xs text-text-muted mt-1">
                    Lançamento interno organizado para digitação rápida por ficha/área IDARON. O relatório
                    final para o Banco do Brasil deve reproduzir integralmente o modelo bancário
                    “SEMOVENTES ATUAIS”.
                  </p>
                </div>
                <div className="flex gap-2 flex-wrap items-center">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => gerarRelatorioSemoventesPdf.mutate()}
                    disabled={gerarRelatorioSemoventesPdf.isPending}
                  >
                    <Printer size={13} /> {gerarRelatorioSemoventesPdf.isPending ? 'Gerando PDF…' : 'Visualizar / Imprimir PDF — Relatório de Semoventes Atuais'}
                  </Button>
                  <Chip tone="info" size="sm">Lançamento por área</Chip>
                  <Chip tone="accent" size="sm">Relatório BB integral</Chip>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-warning/40 bg-warning/5 p-4 text-xs text-text space-y-1">
              <div className="font-bold text-warning">Regra crítica — penhor bovino</div>
              <div>• Animal em penhor <b>não pode ser baixado/omitido</b> do relatório.</div>
              <div>• Sempre solicitar <b>Certidão de Penhor de Bovinos</b>.</div>
              <div>• Consultar os <b>animais em penhor no cadastro do cliente na plataforma BB</b>.</div>
              <div>• Animal que não constar na certidão/cadastro de penhor deve ser tratado como <b>Q – Quitado</b>.</div>
              <div>• Usar somente fichas de rebanho em nome do titular <b>Osny Blanco Dutra</b>; fichas em nome de terceiros ficam fora da consolidação.</div>
              <div>• Se a certidão/BB indicar animais em penhor que não aparecem nas fichas de rebanho, gerar apontamento para ajuste do cliente. Ex.: “faltam XX vacas de 24–36 meses”.</div>
            </div>

            <div className="rounded-lg border border-border bg-surface p-4 space-y-3">
              <div>
                <div className="text-xs font-bold uppercase tracking-wide text-text-muted">
                  1. Lançamento por área / ficha IDARON
                </div>
                <p className="text-xs text-text-muted mt-1">
                  Cada linha representa uma propriedade/ficha sanitária. Digitar as quantidades por sexo e faixa etária;
                  o Hub consolida para o relatório BB e mantém a individualização por área de localização.
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-[1220px] w-full text-[0.68rem] border-collapse">
                  <thead>
                    <tr className="bg-muted/40 text-text-muted">
                      {[
                        'Área / Imóvel de localização',
                        'Vínculo',
                        'Cód. propriedade PGA',
                        'Cód. exploração PGA',
                        '0–6 M', '0–6 F',
                        '7–12 M', '7–12 F',
                        '13–24 M', '13–24 F',
                        '25–36 M', '25–36 F',
                        '+36 M', '+36 F',
                        'Total M', 'Total F', 'Total geral',
                      ].map((h) => (
                        <th key={h} className="border border-border px-2 py-1 text-left font-semibold whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Faz. Baldin', 'Arrendatário', '11000078030', '110000780300008', '0', '90', '1.077', '10', '140', '12', '223', '58', '2', '494', '1.442', '664', '2.106'],
                      ['Sítio Bastos Dutra', 'Proprietário', '11000052346', '110000523460002', '1', '0', '2', '1', '4', '0', '10', '5', '0', '7', '17', '13', '30'],
                      ['Sítio Blanco e Dutra', 'Proprietário', '11000113506', '110001135060010', '0', '0', '21', '0', '64', '0', '83', '19', '19', '4', '187', '23', '210'],
                      ['Faz. Jamantão', 'Arrendatário', '11000046982', '110000469820007', '2', '0', '2', '1', '0', '1', '20', '0', '0', '4', '24', '6', '30'],
                      ['Fazenda Maravilha', 'Proprietário', '11000083012', '110000830120004', '1', '0', '22', '0', '263', '0', '214', '0', '0', '1', '500', '1', '501'],
                    ].map((row, idx) => (
                      <tr key={idx}>
                        {row.map((cell, cidx) => (
                          <td key={cidx} className={`border border-border px-2 py-1 ${cidx >= 4 ? 'text-right tabular-nums' : ''}`}>
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                    <tr className="bg-muted/30 font-bold text-text">
                      {['TOTAL', '', '', '', '4', '90', '1.124', '12', '471', '13', '550', '82', '21', '510', '2.170', '707', '2.877'].map((cell, idx) => (
                        <td key={idx} className={`border border-border px-2 py-1 ${idx >= 4 ? 'text-right tabular-nums' : ''}`}>{cell}</td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="rounded-lg border border-border bg-surface p-4 space-y-3">
              <div>
                <div className="text-xs font-bold uppercase tracking-wide text-text-muted">
                  2. Consolidação sugerida para o relatório BB
                </div>
                <p className="text-xs text-text-muted mt-1">
                  Somar por sexo e idade. No relatório do banco, cada categoria pode virar linha conforme finalidade,
                  situação, gravame e imóvel de localização. O técnico pode ajustar raça, valor unitário, finalidade e gravame.
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-[980px] w-full text-[0.68rem] border-collapse">
                  <thead>
                    <tr className="bg-[var(--accent)] text-white">
                      {['Categoria sugerida', 'Sexo', 'Faixa etária', 'Qtde.', 'Finalidade (*F)', 'Situação (*S)', 'Gravame', 'Imóvel/localização'].map((h) => (
                        <th key={h} className="border border-border px-2 py-1 text-left font-semibold whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Bezerros 0–6 meses', 'M', '0 a 6 meses', '4', '03 – Criação', 'Q se não constar em penhor', 'Não', 'nome da área na ficha'],
                      ['Bezerras 0–6 meses', 'F', '0 a 6 meses', '90', '03 – Criação', 'Q se não constar em penhor', 'Não', 'nome da área na ficha'],
                      ['Machos 7–12 meses', 'M', '7 a 12 meses', '1.124', '05 – Criação/Recriação/Engorda', 'Q se não constar em penhor', 'Não', 'nome da área na ficha'],
                      ['Fêmeas 7–12 meses', 'F', '7 a 12 meses', '12', '03 – Criação', 'Q se não constar em penhor', 'Não', 'nome da área na ficha'],
                      ['Machos 13–24 meses', 'M', '13 a 24 meses', '471', '05 – Criação/Recriação/Engorda', 'Q se não constar em penhor', 'Não', 'nome da área na ficha'],
                      ['Fêmeas 13–24 meses', 'F', '13 a 24 meses', '13', '03 – Criação', 'Q se não constar em penhor', 'Não', 'nome da área na ficha'],
                      ['Machos 25–36 meses', 'M', '25 a 36 meses', '550', '04 – Engorda para abate', 'Q se não constar em penhor', 'Não', 'nome da área na ficha'],
                      ['Fêmeas 25–36 meses', 'F', '25 a 36 meses', '82', '01/03 conforme uso', 'Q se não constar em penhor', 'Não', 'nome da área na ficha'],
                      ['Machos +36 meses', 'M', '+ de 36 meses', '21', '07/12 conforme uso', 'Q se não constar em penhor', 'Não', 'nome da área na ficha'],
                      ['Vacas +36 meses', 'F', '+ de 36 meses', '510', '01 – Produção de crias', 'Q se não constar em penhor', 'Não', 'nome da área na ficha'],
                    ].map((row, idx) => (
                      <tr key={idx}>
                        {row.map((cell, cidx) => (
                          <td key={cidx} className={`border border-border px-2 py-1 ${cidx === 3 ? 'text-right tabular-nums font-semibold' : ''}`}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="rounded-lg border border-border bg-surface p-4 space-y-3">
              <div className="text-xs font-bold uppercase tracking-wide text-text-muted">
                3. Controle de penhor e apontamentos
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-[920px] w-full text-[0.68rem] border-collapse">
                  <thead>
                    <tr className="bg-muted/40 text-text-muted">
                      {['Origem da exigência', 'Categoria/idade/sexo', 'Qtde. em penhor', 'Qtde. localizada nas fichas', 'Diferença', 'Ação'].map((h) => (
                        <th key={h} className="border border-border px-2 py-1 text-left font-semibold whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-border px-2 py-2">Certidão de Penhor de Bovinos / cadastro BB</td>
                      <td className="border border-border px-2 py-2">Ex.: vacas 24–36 meses</td>
                      <td className="border border-border px-2 py-2 text-right">—</td>
                      <td className="border border-border px-2 py-2 text-right">—</td>
                      <td className="border border-border px-2 py-2 text-right">—</td>
                      <td className="border border-border px-2 py-2">Se diferença &gt; 0, gerar apontamento para o cliente ajustar.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="rounded-lg border border-border bg-surface p-4 space-y-3">
              <div className="text-center bg-[var(--accent2)] text-white font-bold py-2 text-xs tracking-wide">
                BENS COM GRAVAMES EM OUTRAS INSTITUIÇÕES FINANCEIRAS
              </div>
              <p className="text-xs text-text-muted">
                Este bloco alimenta o relatório bancário final. Deve conter instituição, CNPJ, data de início, valor e localização
                sempre que houver gravame externo identificado na Certidão de Penhor de Bovinos ou no cadastro BB.
              </p>
              <div className="overflow-x-auto">
                <table className="min-w-[980px] w-full text-[0.68rem] border-collapse">
                  <thead>
                    <tr className="bg-muted/40 text-text-muted">
                      {['Instituição', 'CNPJ', 'Data', 'Bens em penhor', 'Localização', 'Valor'].map((h) => (
                        <th key={h} className="border border-border px-2 py-1 text-left font-semibold whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {semoventesPenhorModelo.map((row, idx) => (
                      <tr key={idx}>
                        {row.map((cell, cidx) => (
                          <td key={cidx} className="border border-border px-2 py-1">{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="rounded-lg border border-border bg-surface p-4 space-y-3">
              <div className="text-center bg-[var(--accent2)] text-white font-bold py-2 text-xs tracking-wide">
                RTA DE PECUÁRIA — APONTAMENTOS COERENTES COM O SISTEMA DE PRODUÇÃO
              </div>
              <ul className="text-xs text-text-muted list-disc pl-5 space-y-1">
                {semoventesRtaPecuariaModelo.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {abaBB === 'documentos' && (
          <div className="space-y-2">
            {data.checklist.map((item, idx) => (
              <div
                key={`${item.nome_documento}-${idx}`}
                className="rounded-lg border border-border bg-surface p-3 flex items-start justify-between gap-3"
              >
                <div>
                  <div className="text-sm font-semibold text-text">{item.nome_documento}</div>
                  <div className="text-xs text-text-muted">
                    Status: {item.status || 'pendente'}
                    {item.observacoes ? ` · ${item.observacoes}` : ''}
                  </div>
                </div>
                {item.arquivo_url ? (
                  <Button variant="secondary" size="sm" asChild>
                    <a href={item.arquivo_url} target="_blank" rel="noreferrer">
                      <FileText size={13} /> Abrir
                    </a>
                  </Button>
                ) : (
                  <Chip tone="warn" size="sm">
                    pendente
                  </Chip>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function BenfeitoriasEditor({
  row,
  saving,
  suggesting,
  onSuggest,
  onSave,
}: {
  row: BBImovelRow;
  saving: boolean;
  suggesting: boolean;
  onSuggest: () => Promise<Benfeitoria[]>;
  onSave: (items: Benfeitoria[]) => Promise<unknown>;
}) {
  const [possui, setPossui] = useState(
    () => Array.isArray(row.benfeitorias) && row.benfeitorias.length > 0,
  );
  const [items, setItems] = useState<Benfeitoria[]>(() => benfeitoriasIniciais(row));
  const totalGeral = items.reduce(
    (sum, item) => sum + nBR(item.quantidade) * nBR(item.valor_unitario),
    0,
  );
  const [suggestMsg, setSuggestMsg] = useState<string | null>(null);

  function update(index: number, field: keyof Benfeitoria, value: string) {
    setItems((cur) => cur.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  }
  function addItem() {
    setPossui(true);
    setItems((cur) => [
      ...cur,
      {
        id: `benf-${Date.now()}`,
        discriminacao: '',
        unidade: '',
        quantidade: '',
        valor_unitario: '',
      },
    ]);
  }
  function removeItem(index: number) {
    setItems((cur) => cur.filter((_, i) => i !== index));
  }
  async function applySuggestions() {
    setSuggestMsg(null);
    const sugestoes = await onSuggest();
    if (!sugestoes.length) {
      setPossui(true);
      setSuggestMsg(
        'Nenhuma referência de valor localizada nos documentos anexados dos bancos. Preencha manualmente e salve.',
      );
      if (!items.length) addItem();
      return;
    }
    setPossui(true);
    setItems((cur) => {
      const preenchidos = cur.filter((i) => i.discriminacao || i.quantidade || i.valor_unitario);
      return [
        ...preenchidos,
        ...sugestoes.map((s, idx) => ({
          id: s.id || `sug-${Date.now()}-${idx}`,
          discriminacao: s.discriminacao || '',
          unidade: s.unidade || '',
          quantidade: String(s.quantidade ?? '1'),
          valor_unitario: String(s.valor_unitario ?? ''),
          valor_total: s.valor_total ?? null,
          fonte: s.fonte ?? null,
          trecho: s.trecho ?? null,
          observacao: s.observacao ?? null,
        })),
      ];
    });
    setSuggestMsg(
      `${sugestoes.length} sugestão(ões) carregada(s) dos documentos anexados. O técnico pode alterar qualquer valor antes de salvar.`,
    );
  }
  async function save() {
    await onSave(possui ? items : []);
  }

  return (
    <div className="rounded border border-border bg-muted/20 p-3 space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-semibold text-text">Imóvel possui benfeitorias?</div>
          <div className="text-[0.72rem] text-text-muted">
            Ao marcar Sim, preencha discriminação, quantidade e valor unitário. O valor total é
            calculado automaticamente e salvo no cadastro do imóvel.
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant={possui ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => {
              setPossui(true);
              if (!items.length) addItem();
            }}
          >
            Sim
          </Button>
          <Button
            variant={!possui ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setPossui(false)}
          >
            Não
          </Button>
          <Button variant="secondary" size="sm" onClick={applySuggestions} disabled={suggesting}>
            <RefreshCw size={13} /> {suggesting ? 'Buscando…' : 'Buscar referência nos documentos'}
          </Button>
        </div>
      </div>
      {suggestMsg && (
        <div className="text-xs text-text-muted rounded border border-border bg-surface px-2 py-1">
          {suggestMsg}
        </div>
      )}

      {possui && (
        <div className="space-y-2">
          <div className="overflow-auto rounded border border-border bg-surface">
            <table className="w-full text-xs border-collapse min-w-[760px]">
              <thead>
                <tr className="text-left text-text-2 border-b border-border bg-surface2">
                  <th className="py-2 px-2">Discriminação</th>
                  <th className="py-2 px-2 w-24">Unid.</th>
                  <th className="py-2 px-2 w-28">Quantidade</th>
                  <th className="py-2 px-2 w-36">Valor unitário</th>
                  <th className="py-2 px-2 w-36">Valor total</th>
                  <th className="py-2 px-2 w-12"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => {
                  const total = nBR(item.quantidade) * nBR(item.valor_unitario);
                  return (
                    <tr key={item.id || index} className="border-b border-border/70">
                      <td className="py-2 px-2">
                        <input
                          value={item.discriminacao}
                          onChange={(e) => update(index, 'discriminacao', e.target.value)}
                          placeholder="Ex.: curral, cerca, casa-sede"
                          className="w-full h-9 rounded border border-border bg-elevated px-2"
                        />
                        {(item.fonte || item.trecho) && (
                          <div className="mt-1 text-[0.68rem] text-text-muted">
                            {item.fonte ? `Fonte: ${item.fonte}` : ''}
                            {item.trecho ? ` · Trecho: ${item.trecho}` : ''}
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-2">
                        <input
                          value={item.unidade || ''}
                          onChange={(e) => update(index, 'unidade', e.target.value)}
                          placeholder="m²/km/un"
                          className="w-full h-9 rounded border border-border bg-elevated px-2"
                        />
                      </td>
                      <td className="py-2 px-2">
                        <input
                          value={item.quantidade}
                          onChange={(e) => update(index, 'quantidade', e.target.value)}
                          inputMode="decimal"
                          className="w-full h-9 rounded border border-border bg-elevated px-2"
                        />
                      </td>
                      <td className="py-2 px-2">
                        <input
                          value={item.valor_unitario}
                          onChange={(e) => update(index, 'valor_unitario', e.target.value)}
                          inputMode="decimal"
                          className="w-full h-9 rounded border border-border bg-elevated px-2"
                        />
                      </td>
                      <td className="py-2 px-2 font-semibold text-text">{moeda(total)}</td>
                      <td className="py-2 px-2">
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="h-8 w-8 rounded border border-border hover:bg-surface2 inline-flex items-center justify-center"
                          aria-label="Remover benfeitoria"
                        >
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <Button variant="secondary" size="sm" onClick={addItem}>
              <Plus size={13} /> Adicionar item
            </Button>
            <div className="flex items-center gap-3">
              <span className="text-xs font-semibold text-text">
                Total das benfeitorias: {moeda(totalGeral)}
              </span>
              <Button variant="primary" size="sm" onClick={save} disabled={saving}>
                <Save size={13} /> {saving ? 'Salvando…' : 'Salvar benfeitorias'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
